import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Loader2, Plus, ChevronsUpDown, Check, Search } from 'lucide-react';
import { cn } from '../../lib/utils';
import QuickAddModal from '../QuickAddModal';

// Searchable Select Component with Quick Add
const SearchableSelectWithAdd = ({ 
  value, 
  onValueChange, 
  options = [], 
  placeholder = "Select...",
  disabled = false,
  loading = false,
  onQuickAdd,
  quickAddType,
  quickAddParentOptions = [],
  fetchMasterData,
  helperText,
  "data-testid": dataTestId
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options;
    const query = searchQuery.toLowerCase();
    return options.filter((opt) => 
      opt.name?.toLowerCase().includes(query) ||
      opt.label?.toLowerCase().includes(query)
    );
  }, [options, searchQuery]);

  const selectedOption = options.find(opt => opt.id === value || opt.value === value);

  if (loading) {
    return (
      <div className="flex gap-1">
        <Button variant="outline" disabled className="w-full justify-between">
          <div className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Loading...</span>
          </div>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex gap-1">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              className={cn(
                "w-full justify-between font-normal",
                !value && "text-muted-foreground"
              )}
              data-testid={dataTestId}
            >
              <span className="truncate">
                {selectedOption?.name || selectedOption?.label || placeholder}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <Command shouldFilter={false}>
              <div className="flex items-center border-b px-3">
                <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                <input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
                />
              </div>
              <CommandList>
                {filteredOptions.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    No results found.
                  </div>
                ) : (
                  <CommandGroup className="max-h-[200px] overflow-y-auto">
                    <CommandItem
                      value="none"
                      onSelect={() => {
                        onValueChange('');
                        setOpen(false);
                        setSearchQuery("");
                      }}
                      className="cursor-pointer"
                    >
                      <Check className={cn("mr-2 h-4 w-4", !value ? "opacity-100" : "opacity-0")} />
                      <span className="text-muted-foreground">{placeholder}</span>
                    </CommandItem>
                    {filteredOptions.map((option) => (
                      <CommandItem
                        key={option.id || option.value}
                        value={option.id || option.value}
                        onSelect={() => {
                          onValueChange(option.id || option.value);
                          setOpen(false);
                          setSearchQuery("");
                        }}
                        className="cursor-pointer"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            value === (option.id || option.value) ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {option.name || option.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        
        {quickAddType && !disabled && (
          <QuickAddModal 
            type={quickAddType} 
            onSuccess={fetchMasterData}
            parentOptions={quickAddParentOptions}
          />
        )}
      </div>
      {helperText && (
        <p className="text-xs text-muted-foreground">{helperText}</p>
      )}
    </div>
  );
};

const AdmissionTab = ({ formData, updateDeskField, masterData, fetchMasterData, readOnly = false, api, isAgent = false }) => {
  const data = formData.admission || {};
  const update = (field, value) => {
    if (readOnly) return;
    updateDeskField('admission', field, value);
  };
  
  const users = masterData.users || [];
  const universities = masterData.universities || [];

  // Cascading dropdown states
  const [campuses, setCampuses] = useState([]);
  const [degreeTypes, setDegreeTypes] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loadingCampuses, setLoadingCampuses] = useState(false);
  const [loadingDegreeTypes, setLoadingDegreeTypes] = useState(false);
  const [loadingPrograms, setLoadingPrograms] = useState(false);

  // Fetch campuses when university changes
  const fetchCampuses = useCallback(async (universityId) => {
    if (!universityId || !api) return;
    setLoadingCampuses(true);
    try {
      const response = await api().get(`/academic-hierarchy/campuses?university_id=${universityId}`);
      setCampuses(response.data);
    } catch (error) {
      console.error('Failed to fetch campuses:', error);
      setCampuses([]);
    } finally {
      setLoadingCampuses(false);
    }
  }, [api]);

  // Fetch degree types when campus changes
  const fetchDegreeTypes = useCallback(async (campusId, universityId) => {
    if (!api) return;
    setLoadingDegreeTypes(true);
    try {
      let url = '/academic-hierarchy/degree-types';
      if (campusId) {
        url += `?campus_id=${campusId}`;
      } else if (universityId) {
        url += `?university_id=${universityId}`;
      }
      const response = await api().get(url);
      setDegreeTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch degree types:', error);
      setDegreeTypes([]);
    } finally {
      setLoadingDegreeTypes(false);
    }
  }, [api]);

  // Fetch programs when degree type changes
  const fetchPrograms = useCallback(async (degreeTypeId, campusId, universityId, savedProgramId = null) => {
    if (!api) return;
    setLoadingPrograms(true);
    try {
      let url = '/academic-hierarchy/programs';
      const params = [];
      if (degreeTypeId) {
        params.push(`degree_type_id=${degreeTypeId}`);
        // r60 — Always pass university_id alongside DT id so the backend can
        // gracefully fall back to a university-scoped query when the saved
        // DT id is orphaned (e.g. legacy records pointing to a deleted DT).
        if (universityId) params.push(`university_id=${universityId}`);
      } else if (campusId) {
        params.push(`campus_id=${campusId}`);
      } else if (universityId) {
        params.push(`university_id=${universityId}`);
      }
      if (params.length > 0) url += '?' + params.join('&');
      
      const response = await api().get(url);
      let programsList = response.data || [];
      
      // If there's a saved program, ensure it's in the list
      if (savedProgramId) {
        const savedProgramExists = programsList.some(p => p.id === savedProgramId);
        if (!savedProgramExists) {
          try {
            const savedProgramResponse = await api().get(`/academic-hierarchy/program/${savedProgramId}`);
            if (savedProgramResponse.data && savedProgramResponse.data.id) {
              console.log('Adding saved program to fetched list:', savedProgramResponse.data);
              programsList = [...programsList, savedProgramResponse.data];
            }
          } catch (err) {
            console.error('Failed to fetch saved program:', err);
          }
        }
      }
      
      setPrograms(programsList);
    } catch (error) {
      console.error('Failed to fetch programs:', error);
      setPrograms([]);
    } finally {
      setLoadingPrograms(false);
    }
  }, [api]);

  // Auto-fill subject and faculty when program changes
  const handleProgramChange = useCallback(async (programId) => {
    update('program', programId);
    
    if (programId && api) {
      try {
        const response = await api().get(`/academic-hierarchy/program/${programId}`);
        if (response.data) {
          if (response.data.subject) update('subject', response.data.subject);
          if (response.data.faculty) update('faculty', response.data.faculty);
        }
      } catch (error) {
        console.error('Failed to fetch program details:', error);
      }
    }
  }, [api, update]);

  // Effect to load cascading data based on existing values
  useEffect(() => {
    if (data.university) fetchCampuses(data.university);
  }, [data.university, fetchCampuses]);

  // Load degree types - always load on mount, and filter when campus/university changes
  useEffect(() => {
    fetchDegreeTypes(data.campus, data.university);
  }, [data.campus, data.university, fetchDegreeTypes]);

  // Load programs - also load all programs if a program is pre-set (from AI Counselor)
  useEffect(() => {
    if (data.degree_type || data.campus || data.university) {
      // Pass the saved program ID to ensure it's included in the list
      fetchPrograms(data.degree_type, data.campus, data.university, data.program);
    } else if (data.program) {
      // If program is set but no university/campus/degree_type, fetch all programs
      // This handles AI Counselor created applications
      const fetchAllPrograms = async () => {
        if (!api) return;
        setLoadingPrograms(true);
        try {
          const response = await api().get('/academic-hierarchy/programs');
          let programsList = response.data || [];
          
          // Ensure the saved program is in the list
          const savedProgramExists = programsList.some(p => p.id === data.program);
          if (!savedProgramExists) {
            try {
              const savedProgramResponse = await api().get(`/academic-hierarchy/program/${data.program}`);
              if (savedProgramResponse.data && savedProgramResponse.data.id) {
                programsList = [...programsList, savedProgramResponse.data];
              }
            } catch (err) {
              console.error('Failed to fetch saved program:', err);
            }
          }
          
          setPrograms(programsList);
        } catch (error) {
          console.error('Failed to fetch all programs:', error);
          setPrograms([]);
        } finally {
          setLoadingPrograms(false);
        }
      };
      fetchAllPrograms();
    }
  }, [data.degree_type, data.campus, data.university, data.program, fetchPrograms, api]);

  // Ensure saved program is included in the dropdown even if not in filtered list
  // Ensure saved program is in the programs list (for AI Counselor-created apps)
  useEffect(() => {
    const ensureSavedProgramInList = async () => {
      if (data.program && api) {
        // Check if saved program is already in list
        const savedProgramInList = programs.some(p => p.id === data.program);
        if (!savedProgramInList) {
          try {
            console.log('Fetching saved program:', data.program);
            const response = await api().get(`/academic-hierarchy/program/${data.program}`);
            console.log('Fetched program response:', response.data);
            if (response.data && response.data.id) {
              // Add saved program to the list
              setPrograms(prev => {
                // Check again to avoid duplicates
                if (prev.some(p => p.id === data.program)) return prev;
                console.log('Adding program to list:', response.data);
                return [...prev, response.data];
              });
            }
          } catch (error) {
            console.error('Failed to fetch saved program:', error);
          }
        }
      }
    };
    ensureSavedProgramInList();
  }, [data.program, api, programs.length]);  // Also trigger when programs list changes

  // Auto-fill subject and faculty when program is pre-filled (e.g., from AI Counselor)
  useEffect(() => {
    const autoFillFromProgram = async () => {
      // Only auto-fill if program is set but subject and faculty are empty
      if (data.program && api && !data.subject && !data.faculty) {
        try {
          const response = await api().get(`/academic-hierarchy/program/${data.program}`);
          if (response.data) {
            if (response.data.subject) update('subject', response.data.subject);
            if (response.data.faculty) update('faculty', response.data.faculty);
          }
        } catch (error) {
          console.error('Failed to auto-fill subject/faculty from program:', error);
        }
      }
    };
    autoFillFromProgram();
  }, [data.program, data.subject, data.faculty, api, update]);

  // Handle university change - reset dependent fields
  const handleUniversityChange = (universityId) => {
    update('university', universityId);
    update('campus', '');
    update('degree_type', '');
    update('program', '');
    update('subject', '');
    update('faculty', '');
    setCampuses([]);
    setDegreeTypes([]);
    setPrograms([]);
    if (universityId) fetchCampuses(universityId);
  };

  // Handle campus change - reset dependent fields
  const handleCampusChange = (campusId) => {
    update('campus', campusId);
    update('degree_type', '');
    update('program', '');
    update('subject', '');
    update('faculty', '');
    setDegreeTypes([]);
    setPrograms([]);
    if (campusId || data.university) fetchDegreeTypes(campusId, data.university);
  };

  // Handle degree type change - reset dependent fields
  const handleDegreeTypeChange = (degreeTypeId) => {
    update('degree_type', degreeTypeId);
    update('program', '');
    update('subject', '');
    update('faculty', '');
    setPrograms([]);
    if (degreeTypeId || data.campus || data.university) {
      fetchPrograms(degreeTypeId, data.campus, data.university);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-primary">Applied University Details</CardTitle>
          {readOnly && <CardDescription><Badge variant="outline">View Only - You don't have permission to edit this desk</Badge></CardDescription>}
        </CardHeader>
        <CardContent>
          {!isAgent && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Admission Officer</Label>
                  <SearchableSelectWithAdd
                    value={data.admission_officer || ''}
                    onValueChange={(v) => update('admission_officer', v)}
                    options={users}
                    placeholder="Select Officer"
                    disabled={readOnly}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Admission Manager</Label>
                  <SearchableSelectWithAdd
                    value={data.admission_manager || ''}
                    onValueChange={(v) => update('admission_manager', v)}
                    options={users}
                    placeholder="Select Manager"
                    disabled={readOnly}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Partner University</Label>
                  <SearchableSelectWithAdd
                    value={data.partner_university || ''}
                    onValueChange={(v) => update('partner_university', v)}
                    options={universities}
                    placeholder="Select University"
                    disabled={readOnly}
                    quickAddType="university"
                    fetchMasterData={fetchMasterData}
                  />
                </div>
              </div>

              <Separator className="my-4" />
            </>
          )}

          {/* Cascading Academic Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* 1. University */}
            <div className="space-y-2">
              <Label>University *</Label>
              <SearchableSelectWithAdd
                value={data.university || ''}
                onValueChange={handleUniversityChange}
                options={universities}
                placeholder="Select University"
                disabled={readOnly}
                quickAddType="university"
                fetchMasterData={fetchMasterData}
                data-testid="university-select"
              />
            </div>
            
            {/* 2. Campus */}
            <div className="space-y-2">
              <Label>Campus</Label>
              <SearchableSelectWithAdd
                value={data.campus || ''}
                onValueChange={handleCampusChange}
                options={campuses}
                placeholder="Select Campus"
                disabled={readOnly || !data.university}
                loading={loadingCampuses}
                quickAddType="campus"
                quickAddParentOptions={universities}
                fetchMasterData={() => { fetchMasterData(); if (data.university) fetchCampuses(data.university); }}
                helperText={!data.university ? "Select university first" : null}
                data-testid="campus-select"
              />
            </div>
            
            {/* 3. Degree Type */}
            <div className="space-y-2">
              <Label>Degree Type</Label>
              <SearchableSelectWithAdd
                value={data.degree_type || ''}
                onValueChange={handleDegreeTypeChange}
                options={degreeTypes}
                placeholder="Select Degree Type"
                disabled={readOnly}
                loading={loadingDegreeTypes}
                quickAddType="degree_type"
                quickAddParentOptions={campuses.length > 0 ? campuses : universities}
                fetchMasterData={() => { fetchMasterData(); fetchDegreeTypes(data.campus, data.university); }}
                data-testid="degree-type-select"
              />
            </div>
            
            {/* 4. Program */}
            <div className="space-y-2">
              <Label>Program</Label>
              <SearchableSelectWithAdd
                value={data.program || ''}
                onValueChange={handleProgramChange}
                options={programs}
                placeholder="Select Program"
                disabled={readOnly}
                loading={loadingPrograms}
                quickAddType="program"
                quickAddParentOptions={degreeTypes.length > 0 ? degreeTypes : (campuses.length > 0 ? campuses : universities)}
                fetchMasterData={() => { fetchMasterData(); if (data.degree_type || data.campus || data.university) fetchPrograms(data.degree_type, data.campus, data.university); }}
                helperText={programs.length === 0 && !loadingPrograms && !data.program ? "Select degree type or university first" : null}
                data-testid="program-select"
              />
            </div>
            
            {/* 5. Subject (Auto-filled) */}
            <div className="space-y-2">
              <Label>Subject <Badge variant="outline" className="ml-2 text-xs">Auto-filled</Badge></Label>
              <Input 
                value={data.subject || ''} 
                onChange={(e) => update('subject', e.target.value)} 
                readOnly={readOnly}
                placeholder="Auto-filled from program"
                className={data.subject ? 'bg-green-50 dark:bg-green-950/20' : ''}
                data-testid="subject-input"
              />
            </div>
            
            {/* 6. Faculty (Auto-filled) */}
            <div className="space-y-2">
              <Label>Faculty <Badge variant="outline" className="ml-2 text-xs">Auto-filled</Badge></Label>
              <Input 
                value={data.faculty || ''} 
                onChange={(e) => update('faculty', e.target.value)} 
                readOnly={readOnly}
                placeholder="Auto-filled from program"
                className={data.faculty ? 'bg-green-50 dark:bg-green-950/20' : ''}
                data-testid="faculty-input"
              />
            </div>
          </div>
          
          <Separator className="my-4" />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Session Month</Label>
              <Select value={data.session_month || 'none'} onValueChange={(v) => update('session_month', v === 'none' ? '' : v)} disabled={readOnly}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select</SelectItem>
                  <SelectItem value="january">January</SelectItem>
                  <SelectItem value="february">February</SelectItem>
                  <SelectItem value="march">March</SelectItem>
                  <SelectItem value="april">April</SelectItem>
                  <SelectItem value="may">May</SelectItem>
                  <SelectItem value="june">June</SelectItem>
                  <SelectItem value="july">July</SelectItem>
                  <SelectItem value="august">August</SelectItem>
                  <SelectItem value="september">September</SelectItem>
                  <SelectItem value="october">October</SelectItem>
                  <SelectItem value="november">November</SelectItem>
                  <SelectItem value="december">December</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Session Year</Label>
              <Input value={data.session_year || ''} onChange={(e) => update('session_year', e.target.value)} placeholder="e.g., 2024" readOnly={readOnly} />
            </div>
            <div className="space-y-2">
              <Label>CAS Processing Status</Label>
              <Select value={data.cas_processing_status || 'none'} onValueChange={(v) => update('cas_processing_status', v === 'none' ? '' : v)} disabled={readOnly}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select</SelectItem>
                  <SelectItem value="not_started">Not Started</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="applied">Applied</SelectItem>
                  <SelectItem value="issued">Issued</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <Label>Admission Notes</Label>
            <Textarea value={data.notes || ''} onChange={(e) => update('notes', e.target.value)} rows={3} readOnly={readOnly} />
          </div>
        </CardContent>
      </Card>

      {!isAgent && (
      <Card>
        <CardHeader><CardTitle className="text-primary">Offer Letter / CAS Details</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2"><Label>University Reference Number</Label><Input value={data.university_ref || ''} onChange={(e) => update('university_ref', e.target.value)} readOnly={readOnly} /></div>
            <div className="space-y-2"><Label>Apply Date to Uni</Label><Input type="date" value={data.apply_date || ''} onChange={(e) => update('apply_date', e.target.value)} readOnly={readOnly} /></div>
            <div className="space-y-2"><Label>Date of Offer</Label><Input type="date" value={data.date_of_offer || ''} onChange={(e) => update('date_of_offer', e.target.value)} readOnly={readOnly} /></div>
            <div className="space-y-2"><Label>Currency</Label><Select value={data.currency || 'none'} onValueChange={(v) => update('currency', v === 'none' ? '' : v)} disabled={readOnly}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="none">Select</SelectItem><SelectItem value="GBP">GBP (£)</SelectItem><SelectItem value="USD">USD ($)</SelectItem><SelectItem value="EUR">EUR (€)</SelectItem><SelectItem value="AUD">AUD (A$)</SelectItem><SelectItem value="CAD">CAD (C$)</SelectItem></SelectContent></Select></div>
            <div className="space-y-2"><Label>Total Fee on Offer</Label><Input type="number" value={data.total_fee_offer || ''} onChange={(e) => update('total_fee_offer', e.target.value)} readOnly={readOnly} /></div>
            <div className="space-y-2"><Label>Scholarship on Offer</Label><Input value={data.scholarship_offer || ''} onChange={(e) => update('scholarship_offer', e.target.value)} readOnly={readOnly} /></div>
            <div className="space-y-2"><Label>Net Fee on Offer</Label><Input type="number" value={data.net_fee_offer || ''} onChange={(e) => update('net_fee_offer', e.target.value)} readOnly={readOnly} /></div>
            <div className="space-y-2"><Label>Deposit Date</Label><Input type="date" value={data.deposit_date || ''} onChange={(e) => update('deposit_date', e.target.value)} readOnly={readOnly} /></div>
            <div className="space-y-2"><Label>CAS/COE Applied Date</Label><Input type="date" value={data.cas_applied_date || ''} onChange={(e) => update('cas_applied_date', e.target.value)} readOnly={readOnly} /></div>
          </div>
          <Separator className="my-4" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2"><Label>CAS/COE Issued</Label><Select value={data.cas_issued || 'none'} onValueChange={(v) => update('cas_issued', v === 'none' ? '' : v)} disabled={readOnly}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="none">Select</SelectItem><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem><SelectItem value="pending">Pending</SelectItem></SelectContent></Select></div>
            <div className="space-y-2"><Label>Total Fee on CAS</Label><Input type="number" value={data.total_fee_cas || ''} onChange={(e) => update('total_fee_cas', e.target.value)} readOnly={readOnly} /></div>
            <div className="space-y-2"><Label>Balance Fee after CAS</Label><Input type="number" value={data.balance_fee_cas || ''} onChange={(e) => update('balance_fee_cas', e.target.value)} readOnly={readOnly} /></div>
            <div className="space-y-2"><Label>Status of Offer</Label><Select value={data.offer_status || 'none'} onValueChange={(v) => update('offer_status', v === 'none' ? '' : v)} disabled={readOnly}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="none">Select</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="conditional">Conditional</SelectItem><SelectItem value="unconditional">Unconditional</SelectItem><SelectItem value="accepted">Accepted</SelectItem><SelectItem value="rejected">Rejected</SelectItem></SelectContent></Select></div>
          </div>
        </CardContent>
      </Card>
      )}
    </div>
  );
};

export default AdmissionTab;
